<template>
  <!-- ════════════════════════════════════════════════════════════════
       RecommendedNews.vue
       ສະແດງຂ່າວສານລ່າສຸດ 3 ອັນ — ໃຊ້ໃນ HomePage.vue
  ════════════════════════════════════════════════════════════════ -->
  <section class="recommended-news">
    <!-- Section Header -->
    <div class="section-header">
      <div class="title-wrapper">
        <h2 class="section-title">ຂ່າວສານລ່າສຸດ</h2>
        <div class="title-underline"></div>
      </div>
    </div>

    <!-- Loading Skeleton -->
    <div v-if="loading" class="skeleton-grid">
      <div v-for="n in 3" :key="n" class="skeleton-card">
        <div class="skeleton-image"></div>
        <div class="skeleton-body">
          <div class="skeleton-line badge"></div>
          <div class="skeleton-line title"></div>
          <div class="skeleton-line title short"></div>
          <div class="skeleton-footer"></div>
        </div>
      </div>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="error-state">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <circle cx="12" cy="12" r="10"></circle>
        <line x1="12" y1="16" x2="12" y2="12"></line>
        <line x1="12" y1="8" x2="12.01" y2="8"></line>
      </svg>
      <p>ຂໍອະໄພ ພົບປັນຫາໃນການໂຫຼດຂ່າວສານໃນຂະນະນີ້</p>
    </div>

    <!-- News Cards -->
    <template v-else>
      <div class="news-grid">
        <article
          v-for="newsItem in latestNews"
          :key="newsItem.id"
          class="news-card"
          @click="goToNews(newsItem.slug)"
        >
          <!-- Image -->
          <div class="card-image">
            <img
              :src="newsItem.featured_image || fallbackImage"
              :alt="newsItem.title"
              loading="lazy"
              @error="handleImageError"
            />
            <div v-if="newsItem.category" class="category-badge">
              {{ newsItem.category.name }}
            </div>
          </div>

          <!-- Content -->
          <div class="card-content">
            <h3 class="card-title">{{ newsItem.title }}</h3>
            <div class="card-footer">
              <time class="card-date">{{
                formatDate(newsItem.published_at)
              }}</time>
              <span class="read-more">
                ອ່ານເພີ່ມເຕີມ
                <svg
                  viewBox="0 -960 960 960"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="m560-240-56-58 142-142H160v-80h486L504-662l56-58 240 240-240 240Z"
                  />
                </svg>
              </span>
            </div>
          </div>
        </article>
      </div>

      <!-- View All Button -->
      <div class="view-all-wrapper">
        <button class="view-all-btn" @click="goToNewsPage">
          ຂ່າວສານທັງໝົດ
          <svg viewBox="0 -960 960 960" xmlns="http://www.w3.org/2000/svg">
            <path
              d="m560-240-56-58 142-142H160v-80h486L504-662l56-58 240 240-240 240Z"
            />
          </svg>
        </button>
      </div>
    </template>
  </section>
</template>

<script setup>
import { computed } from "vue";
import { useRouter } from "vue-router";
import { useQuery } from "@vue/apollo-composable";
import gql from "graphql-tag";

const router = useRouter();

const fallbackImage =
  "https://t3.ftcdn.net/jpg/02/72/45/58/360_F_272455816_mrPQJvA0gKafenXR2o78wR7a7bRytodW.jpg";

// ─── GraphQL — ดึงแค่ 3 อัน limit ที่ query เลย ──────────────────────────────
const GET_LATEST_NEWS = gql`
  query GetLatestNews {
    news(
      where: { status: { _eq: "published" } }
      order_by: { published_at: desc }
      limit: 3
    ) {
      id
      title
      slug
      featured_image
      published_at
      category {
        id
        name
      }
    }
  }
`;

const { result, loading, error } = useQuery(GET_LATEST_NEWS, null, {
  fetchPolicy: "network-only",
});

const latestNews = computed(() => result.value?.news ?? []);

// ─── Helpers ──────────────────────────────────────────────────────────────────
const LAO_MONTHS = [
  "ມັງກອນ",
  "ກຸມພາ",
  "ມີນາ",
  "ເມສາ",
  "ພຶດສະພາ",
  "ມິຖຸນາ",
  "ກໍລະກົດ",
  "ສິງຫາ",
  "ກັນຍາ",
  "ຕຸລາ",
  "ພະຈິກ",
  "ທັນວາ",
];

function formatDate(dateString) {
  if (!dateString) return "";
  const d = new Date(dateString);
  if (isNaN(d.getTime())) return dateString;
  return `${d.getDate()} ${LAO_MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

function handleImageError(event) {
  event.target.src = fallbackImage;
}

function goToNews(slug) {
  router.push({ name: "NewsDetail", params: { slug } });
}

function goToNewsPage() {
  router.push({ name: "News" });
}
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Noto+Sans+Lao:wght@300;400;500;600;700;800&display=swap");

/* ─── Section Wrapper ───────────────────────────────────────────────────── */
.recommended-news {
  font-family: "Noto Sans Lao", "Phetsarath OT", sans-serif;
  background-color: #ffffff;
  padding: 5rem 1.5rem;
  width: 100%;
  box-sizing: border-box;
}

/* ─── Section Header ────────────────────────────────────────────────────── */
.section-header {
  text-align: center;
  margin-bottom: 3.5rem;
}

.section-title {
  font-size: clamp(1.6rem, 3.5vw, 2.2rem);
  font-weight: 800;
  color: #344873;
  margin: 0 0 0.75rem;
  letter-spacing: -0.01em;
}
.title-wrapper {
  display: inline-block;
}

.title-underline {
  width: 100%;
  height: 0.25rem;
  background: #ea2127;
  border-radius: 99px;
  margin: 0 auto;
}

/* ─── Grid ──────────────────────────────────────────────────────────────── */
.news-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.875rem;
  max-width: 75rem;
  margin: 0 auto;
}

/* ─── Card ──────────────────────────────────────────────────────────────── */
.news-card {
  background: #ffffff;
  border-radius: 1rem;
  overflow: hidden;
  box-shadow: 0 0.625rem 0.9375rem -0.1875rem rgba(0, 0, 0, 0.08);
  cursor: pointer;
  display: flex;
  flex-direction: column;
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1),
    box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.news-card:hover {
  transform: translateY(-0.75rem);
  box-shadow: 0 1.25rem 1.5625rem -0.3125rem rgba(0, 0, 0, 0.12);
}

/* ─── Card Image ────────────────────────────────────────────────────────── */
.card-image {
  position: relative;
  height: 13rem;
  overflow: hidden;
  background: #f0f0f0;
  flex-shrink: 0;
}

.card-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.4s ease;
}

.news-card:hover .card-image img {
  transform: scale(1.05);
}

.category-badge {
  position: absolute;
  top: 0.75rem;
  left: 0.75rem;
  padding: 0.375rem 0.875rem;
  background: rgba(234, 33, 39, 0.85);
  color: #ffffff;
  border-radius: 1rem;
  font-size: 0.8rem;
  font-weight: 600;
  backdrop-filter: blur(4px);
  letter-spacing: 0.02em;
}

/* ─── Card Content ──────────────────────────────────────────────────────── */
.card-content {
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  flex: 1;
}

.card-title {
  font-size: 1.05rem;
  font-weight: 700;
  color: #344873;
  line-height: 1.5;
  margin: 0 0 auto;
  padding-bottom: 0.875rem;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* ─── Card Footer ───────────────────────────────────────────────────────── */
.card-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 0.875rem;
  border-top: 1px solid #e2e8f0;
  margin-top: 0.875rem;
}

.card-date {
  font-size: 0.82rem;
  color: #718096;
}

.read-more {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.82rem;
  font-weight: 600;
  color: #344873;
  transition: color 0.25s ease;
}

.read-more svg {
  width: 1rem;
  height: 1rem;
  fill: currentColor;
  transition: transform 0.25s ease;
}

.news-card:hover .read-more {
  color: #ea2127;
}

.news-card:hover .read-more svg {
  transform: translateX(3px);
}

/* ─── View All Button ───────────────────────────────────────────────────── */
.view-all-wrapper {
  display: flex;
  justify-content: center;
  margin-top: 3rem;
}

.view-all-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: linear-gradient(135deg, #ea2127 0%, #b7191e 100%);
  color: #ffffff;
  border: none;
  padding: 0.875rem 2.5rem;
  border-radius: 50px;
  font-family: inherit;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  letter-spacing: 0.02em;
}

.view-all-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 0.625rem 0.9375rem -0.1875rem rgba(0, 0, 0, 0.12);
}

.view-all-btn svg {
  width: 1.2rem;
  height: 1.2rem;
  fill: #ffffff;
}

/* ─── Loading Skeleton ──────────────────────────────────────────────────── */
.skeleton-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.875rem;
  max-width: 75rem;
  margin: 0 auto;
}

.skeleton-card {
  background: #ffffff;
  border-radius: 1.25rem;
  overflow: hidden;
  box-shadow: 0 0.625rem 0.9375rem -0.1875rem rgba(0, 0, 0, 0.06);
}

.skeleton-image {
  height: 13rem;
  background: linear-gradient(90deg, #e8eaed 25%, #f4f5f7 50%, #e8eaed 75%);
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite;
}

.skeleton-body {
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
}

.skeleton-line {
  height: 0.875rem;
  border-radius: 0.5rem;
  background: linear-gradient(90deg, #e8eaed 25%, #f4f5f7 50%, #e8eaed 75%);
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite;
}

.skeleton-line.badge {
  width: 35%;
  height: 1.4rem;
  border-radius: 1rem;
}
.skeleton-line.title {
  width: 90%;
}
.skeleton-line.short {
  width: 55%;
}

.skeleton-footer {
  height: 2rem;
  margin-top: 0.5rem;
  border-radius: 0.5rem;
  background: linear-gradient(90deg, #e8eaed 25%, #f4f5f7 50%, #e8eaed 75%);
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite;
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

/* ─── Error State ───────────────────────────────────────────────────────── */
.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 1rem;
  color: #718096;
  gap: 1rem;
}

.error-state svg {
  width: 3.5rem;
  height: 3.5rem;
  color: #cbd5e0;
  stroke-width: 1.5;
}

.error-state p {
  font-size: 1rem;
  margin: 0;
}

/* ─── Responsive ────────────────────────────────────────────────────────── */
@media (max-width: 64rem) {
  .news-grid,
  .skeleton-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 40rem) {
  .recommended-news {
    padding: 2.5rem 1rem 3rem;
  }

  .news-grid,
  .skeleton-grid {
    grid-template-columns: 1fr;
    max-width: 28rem;
  }
}
</style>